import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DataTable, { createTheme } from "react-data-table-component";
import { Alert, Dropdown, Button, Form, Spinner } from "react-bootstrap";
import {
  DeleteInviteRequest,
  ResendInviteRequest,
  changePage,
  changePerPage,
  getInvitesList,
  getStationsList,
  searchInvite,
  toggleAdsWizsAds,
  toggleAudioAds,
  toggleClassicAds,
  togglePodcast,
  toggleVideoAdsAds,
  updateNotificationsToggle,
} from "../../../store/slices/invitesSlice";
import Badge from "react-bootstrap/Badge";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { customStyles } from "../../../components/Table/tableTheme";
import PageLoader from "../../../components/Loader/PageLoader";
import CustomPagination from "../../../components/Pagination/CustomPagination";
import { PROJECT_LIST, STATION_GROUPS } from "../../../constants";
import { FaEdit, FaUsers, FaSearch, FaPlus, FaPodcast } from "react-icons/fa";
import ProjectSelectPopup from "../../../components/models/ProjectSelectPopup";
import ModifyInvitePopup from "../../../components/models/ModifyInvitePopup";
import StationGroupPopup from "../../../components/models/StationGroupPopup";
import UserLookupPopup from "../../../components/models/UserLookupPopup";
import AddMember from "../../../components/models/AddMember";
import StationInvite from "../../../components/models/StationInvite";
import { FiRefreshCw, FiTrash2 } from "react-icons/fi";
import SweetAlert from "../../../components/SweetAlert/SweetAlert";
import Swal from "sweetalert2";
import CreateStationAdmin from "../../../components/models/CreateStationAdmin";
import { getUserRoleInfo } from "../../../helpers/roleUtils";
import RoleManagement from "../../../components/models/RoleManagement";

// Theme
createTheme(
  "solarizedLight",
  {
    text: {
      primary: "#2c3e50",
      secondary: "#7f8c8d",
    },
    background: {
      default: "#fff",
    },
    context: {
      background: "#f39c12",
      text: "#FFFFFF",
    },
    divider: {
      default: "#bdc3c7",
    },
    action: {
      button: "rgba(0,0,0,0.6)",
      hover: "rgba(0,0,0,0.1)",
      disabled: "rgba(0,0,0,0.3)",
    },
  },
  "light"
);

const List = () => {
  const dispatch = useDispatch();
  const { users, error, loading, meta } = useSelector((state) => state.invites);
  const user = useSelector((state) => state.auth.user);
  const { isReseller } = getUserRoleInfo(user || {});

  const page = meta?.page || 1;
  const perPage = meta?.perPage;
  const pages = meta?.pages || 1;

  const [loadingToggles, setLoadingToggles] = useState({});
  const [projectPopup, setProjectPopup] = useState({ show: false, row: null });
  const [modifyPopup, setModifyPopup] = useState({ show: false, row: null });
  const [stationGroupPopup, setStationGroupPopup] = useState({
    show: false,
    row: null,
  });
  const [role, setRole] = useState({ show: false, row: null });

  const [addMemberPopup, setAddMemberPopup] = useState({
    show: false,
    row: null,
  });
  const [userLookupPopup, setUserLookupPopup] = useState({
    show: false,
    id: null,
  });
  const [stationInvitePopup, setStationInvitePopup] = useState({ show: false });
  const [stationAdminCreatePopup, setStationAdminCreatePopup] = useState({
    show: false,
  });

  const [selectedProject, setSelectedProject] = useState("");

  const handlePageChange = (page) => {
    dispatch(changePage(page));
    dispatch(getInvitesList());
  };

  const handlePerRowsChange = (newPerPage, page) => {
    dispatch(changePage(page));
    dispatch(changePerPage(newPerPage)); // Reset to the first page when per page changes
    dispatch(getInvitesList());
  };

  const handlePodcastToggle = async (row, key) => {
    const loadingKey = `${row.id}_${key}`;
    setLoadingToggles((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      const updatedValue = !row.attributes?.metadata?.["3"]?.enablePodcast;

      await dispatch(
        togglePodcast({
          message: "Podcast Status Updated.",
          payload: {
            id: row.attributes.id,
            type: "user",
            attributes: {
              enablePodcast: updatedValue,
            },
          },
        })
      );
    } catch (err) {
      console.error("Toggle failed", err);
    } finally {
      setLoadingToggles((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleAudioDirect = async (row, key, value) => {
    const loadingKey = `${row.id}_${key}`;
    setLoadingToggles((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      await dispatch(
        toggleAudioAds({
          message: "Audio Ad Status Updated.",
          payload: {
            id: row.attributes.id,
            type: "user",
            attributes: {
              enableAudioAds: !value,
            },
          },
        })
      );
    } catch (err) {
      console.error("Toggle failed", err);
    } finally {
      setLoadingToggles((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleAdsWizsToggle = async (row, key, value) => {
    const loadingKey = `${row.id}_${key}`;
    setLoadingToggles((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      await dispatch(
        toggleAdsWizsAds({
          message: "Status Updated.",
          payload: {
            id: row.attributes.id,
            type: "user",
            attributes: {
              enableDisableAdsWizz: !value,
            },
          },
        })
      );
    } catch (err) {
      console.error("Toggle failed", err);
    } finally {
      setLoadingToggles((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleVideoAdsToggle = async (row, key, value) => {
    const loadingKey = `${row.id}_${key}`;
    setLoadingToggles((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      await dispatch(
        toggleVideoAdsAds({
          message: "Status Updated.",
          payload: {
            id: row.attributes.id,
            type: "user",
            attributes: {
              enableVideoAds: !value,
            },
          },
        })
      );
    } catch (err) {
      console.error("Toggle failed", err);
    } finally {
      setLoadingToggles((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleClassicAdsToggle = async (row, key, value) => {
    const loadingKey = `${row.id}_${key}`;
    setLoadingToggles((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      await dispatch(
        toggleClassicAds({
          message: "Status Updated.",
          payload: {
            id: row.attributes.id,
            type: "user",
            attributes: {
              enableAds: !value,
            },
          },
        })
      );
    } catch (err) {
      console.error("Toggle failed", err);
    } finally {
      setLoadingToggles((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleNotificationToggle = async (row, key) => {
    const loadingKey = `${row.id}_${key}`;
    setLoadingToggles((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      await dispatch(
        updateNotificationsToggle({
          message: "Status Updated.",
          payload: {
            id: row.attributes.id,
            type: "user",
            attributes: {
              enableNotification: false,
              firebaseProject: [],
            },
          },
        })
      );
    } catch (err) {
      console.error("Toggle failed", err);
    } finally {
      setLoadingToggles((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const deleteInvite = async (id) => {
    SweetAlert({
      title: "Confirm Your Action",
      text: `Delete Invite for "${id}"?`,
      confirmAction: async () => {
        Swal.showLoading();
        await dispatch(
          DeleteInviteRequest({
            message: "Invite deleted Successfully.",
            id,
          })
        )
          .unwrap()
          .then(() => {
            dispatch(getInvitesList());
          });
      },
    });
  };

  const resendInvitations = async (id) => {
    SweetAlert({
      title: "Confirm Your Action",
      text: `Do you want to send invite to "${id}" one more time?`,
      confirmAction: async () => {
        Swal.showLoading();
        const payload = {
          type: "regenerate-token",
          attributes: {
            challengeType: "email",
            id,
            action: "invite",
          },
        };

        await dispatch(
          ResendInviteRequest({
            message: "Invite Sent Successfully.",
            payload,
          })
        )
          .unwrap()
          .then(() => {
            dispatch(getInvitesList());
          });
      },
    });
  };

  const search = async (input) => {
    dispatch(searchInvite(input));
    dispatch(getInvitesList());
  };

  useEffect(() => {
    dispatch(getInvitesList());
    dispatch(getStationsList());
  }, [dispatch]);

  const columns = [
    {
      name: "#",
      cell: (row, index) => (meta?.page - 1) * meta?.perPage + index + 1,
      width: "60px",
      center: "true",
    },
    {
      name: "Name & Email",
      cell: (row) => (
        <div className="d-flex flex-column f-12">
          <div>
            <strong>
              {row.attributes?.metadata?.["3"]?.firstName || ""}{" "}
              {row.attributes?.metadata?.["3"]?.lastName || ""}
            </strong>
          </div>
          <div>
            <small className="text-muted">
              {row.attributes?.metadata?.["3"]?.email || ""}
            </small>
          </div>
        </div>
      ),
      width: "180px",
      wrap: "true",
    },
    {
      name: "Organization",
      selector: (row) => row.attributes?.metadata?.["3"]?.school || "N/A",
      wrap: "true",
      center: "true",
    },
    {
      name: "Group",
      cell: (row) => {
        const isStationAdmin = row.attributes?.metadata?.["3"].isStationAdmin || false;
        const allotedStationGroups = row.attributes?.metadata?.["3"].allotedStationGroups || "";

        return (
          <div className="d-flex flex-column">
            <Badge bg="success" className="mb-1">
              {STATION_GROUPS[row.attributes?.metadata?.["1"].api.stationGroup]}
            </Badge>
            <Badge bg="primary" className="mb-1">
              {row.attributes?.metadata?.["3"]?.roles?.includes(
                "group_admin"
              ) && "Is Group Admin"}
            </Badge>
            {isStationAdmin && (
              <Badge bg="info" className="mb-1">
                Station Admin
              </Badge>
            )}
            {isStationAdmin && allotedStationGroups && (
              <div className="mt-1">
                <small className="text-muted d-block mb-1">
                  Assigned Stations:
                </small>
                <div className="d-flex flex-wrap gap-1">
                  {allotedStationGroups
                    .split(",")
                    .filter(Boolean)
                    .map((station, index) => (
                      <Badge key={index} bg="secondary" className="text-wrap">
                        {station.trim()}
                      </Badge>
                    ))}
                </div>
              </div>
            )}
          </div>
        );
      },
      wrap: "true",
    },

    {
      name: "Sent",
      cell: (row) => new Date(row.attributes?.created).toLocaleDateString(),
    },
    {
      name: "status",
      cell: (row) => (
        <div className="ad-settings-container">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span className="me-2 small">Accepted :</span>
            <div className="d-flex align-items-center">
              <span className="small">
                {row.attributes?.verified ? "Yes" : "No"}
              </span>
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span className="me-2 small">Published :</span>
            <div className="d-flex align-items-center">
              <span className="small">
                {row.attributes?.metadata?.["3"]?.alias ? "Yes" : "No"}
              </span>
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-between">
            <span className="me-2 small">Hidden :</span>
            <div className="d-flex align-items-center">
              <span className="small">
                {row.attributes?.metadata?.["3"]?.hidden ? "Yes" : "No"}
              </span>
            </div>
          </div>
        </div>
      ),
      minwidth: "150px",
    },

    {
      name: "AdMax Settings",
      cell: (row) => {
        if (isReseller) return <></>;
        if (!row.attributes?.verified) return "Not Available";

        return (
          <div className="ad-settings-container">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="me-2 small">Classic :</span>
              <div className="d-flex align-items-center  position-relative min-w-61">
                {loadingToggles[`${row.id}_enableAds`] && (
                  <Spinner
                    animation="border"
                    className="inner-spinner"
                    size="sm"
                  />
                )}
                <Form.Check
                  type="switch"
                  id={`ads-switch-${row.id}`}
                  checked={!!row.attributes?.metadata?.["3"]?.enableAds}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleClassicAdsToggle(
                      row,
                      "enableAds",
                      row.attributes?.metadata?.["3"]?.enableAds
                    );
                  }}
                />
                <span className="small">
                  {row.attributes?.metadata?.["3"]?.enableAds ? "ON" : "OFF"}
                </span>
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="me-2 small">Video :</span>
              <div className="d-flex align-items-center  position-relative min-w-61">
                {loadingToggles[`${row.id}_enableVideoAds`] && (
                  <Spinner
                    animation="border"
                    className="inner-spinner"
                    size="sm"
                  />
                )}
                <Form.Check
                  type="switch"
                  id={`podcast-switch-${row.id}`}
                  checked={!!row.attributes?.metadata?.["3"]?.enableVideoAds}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleVideoAdsToggle(
                      row,
                      "enableVideoAds",
                      row.attributes?.metadata?.["3"]?.enableVideoAds
                    );
                  }}
                />
                <span className="small">
                  {row.attributes?.metadata?.["3"]?.enableVideoAds
                    ? "ON"
                    : "OFF"}
                </span>
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="me-2 small">Audio:</span>
              <div className="d-flex align-items-center  position-relative min-w-61">
                {loadingToggles[`${row.id}_enableDisableAdsWizz`] && (
                  <Spinner
                    animation="border"
                    className="inner-spinner"
                    size="sm"
                  />
                )}
                <Form.Check
                  type="switch"
                  id={`podcast-switch-${row.id}`}
                  checked={
                    !!row.attributes?.metadata?.["3"]?.enableDisableAdsWizz
                  }
                  onChange={(e) => {
                    e.stopPropagation();
                    handleAdsWizsToggle(
                      row,
                      "enableDisableAdsWizz",
                      row.attributes?.metadata?.["3"]?.enableDisableAdsWizz
                    );
                  }}
                />
                <span className="small">
                  {row.attributes?.metadata?.["3"]?.enableDisableAdsWizz
                    ? "ON"
                    : "OFF"}
                </span>
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="me-2 small">Audio Direct:</span>
              <div className="d-flex align-items-center  position-relative min-w-61">
                {loadingToggles[`${row.id}_enableAudioAds`] && (
                  <Spinner
                    animation="border"
                    className="outer-spinner"
                    size="sm"
                  />
                )}

                <Form.Check
                  type="switch"
                  id={`audio-ads-switch-${row.id}`}
                  checked={!!row.attributes?.metadata?.["3"]?.enableAudioAds}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleAudioDirect(
                      row,
                      "enableAudioAds",
                      row.attributes?.metadata?.["3"]?.enableAudioAds
                    );
                  }}
                />
                <span className="small">
                  {row.attributes?.metadata?.["3"]?.enableAudioAds
                    ? "ON"
                    : "OFF"}
                </span>
              </div>
            </div>
          </div>
        );
      },
      minwidth: "180px",
      center: "true",
    },
    {
      name: "Notifications",
      cell: (row) => {
        if (!row.attributes?.verified) return "Not Available";

        const metadata = row.attributes?.metadata?.["3"];
        const firebaseProjects = metadata?.firebaseProject || [];
        if (isReseller) return <></>;
        return (
          <div className="ad-settings-container w-100">
            <span className="me-2 small">Status :</span>
            <div className="d-flex flex-column align-items-start position-relative">
              {loadingToggles[`${row.id}_enableNotification`] && (
                <Spinner
                  animation="border"
                  className="outer-spinner"
                  style={{ top: "6px", left: "40px" }}
                  size="sm"
                />
              )}
              {!row.attributes?.metadata?.["3"]?.enableNotification ? (
                <Form.Check
                  type="switch"
                  id={`notification-switch-${row.id}`}
                  checked={!!metadata?.enableNotification}
                  onChange={(e) => {
                    e.stopPropagation();
                    setProjectPopup({ show: true, row });
                  }}
                />
              ) : (
                <Form.Check
                  type="switch"
                  id={`notification-switch-${row.id}`}
                  checked={
                    !!row.attributes?.metadata?.["3"]?.enableNotification
                  }
                  onChange={(e) => {
                    e.stopPropagation();
                    handleNotificationToggle(
                      row,
                      "enableNotification",
                      row.attributes?.metadata?.["3"]?.enableNotification
                    );
                  }}
                />
              )}
              <div className="d-flex flex-wrap flex-column gap-1 mt-1">
                {(() => {
                  const projects = Array.isArray(firebaseProjects)
                    ? firebaseProjects
                    : typeof firebaseProjects === "string" &&
                      firebaseProjects.length > 0
                    ? [firebaseProjects]
                    : [];

                  return projects.length > 0 ? (
                    projects.map((projectId, index) => {
                      const project = PROJECT_LIST?.find(
                        (p) => p.projectId === projectId
                      );
                      return (
                        <Badge bg="success" key={index}>
                          {project?.projectName || projectId}
                        </Badge>
                      );
                    })
                  ) : (
                    <Badge bg="secondary">N/A</Badge>
                  );
                })()}
              </div>
            </div>
          </div>
        );
      },
      minwidth: "180px",
      center: "true",
    },
    {
      name: "Actions",
      cell: (row) => (
        <Dropdown
          className="action-dropdown"
          autoClose="outside"
          renderMenuOnMount
          popperConfig={{
            strategy: "fixed",
            modifiers: [
              { name: "preventOverflow", options: { boundary: document.body } },
            ],
          }}
        >
          <Dropdown.Toggle
            variant="light"
            size="sm"
            className="btn btn-outline-primary"
          >
            Action
          </Dropdown.Toggle>

          {row.attributes?.verified ? (
            <Dropdown.Menu className="custom-action-menu shadow">
              {/* Section Header */}
              <Dropdown.Header className="text-muted">
                User Management
              </Dropdown.Header>
              
              <Dropdown.Item
                onClick={(e) => {
                  e.stopPropagation();
                  setModifyPopup({ show: true, row });
                }}
              >
                <FaEdit className="me-2 text-primary" />
                {row.attributes?.metadata?.["3"]?.alias ? "Modify" : "Publish"}
              </Dropdown.Item>

              {!isReseller && (
                <Dropdown.Item
                  onClick={(e) => {
                    e.stopPropagation();
                    setStationGroupPopup({ show: true, row });
                  }}
                >
                  <FaUsers className="me-2 text-success" />
                  Update Station Group
                </Dropdown.Item>
              )}

              <Dropdown.Item
                onClick={(e) => {
                  e.stopPropagation();
                  setUserLookupPopup({ show: true, id: row.attributes.id });
                }}
              >
                <FaSearch className="me-2 text-info" />
                User Details
              </Dropdown.Item>

              {!isReseller && (
                <Dropdown.Item
                  onClick={(e) => {
                    e.stopPropagation();
                    setAddMemberPopup({ show: true, row });
                  }}
                >
                  <FaPlus className="me-2 text-secondary" />
                  Add member
                </Dropdown.Item>
              )}
             {!isReseller && (
                <Dropdown.Item
                  onClick={(e) => {
                    e.stopPropagation();
                    setRole({ show: true, row });
                  }}
                >
                  <FaUsers className="me-2 text-success" />
                  Manage Role
                </Dropdown.Item>
              )}
              {!isReseller && (
                <>
                  <Dropdown.Divider />
                  <Dropdown.Header className="text-muted">
                    Others
                  </Dropdown.Header>
                </>
              )}

              {!isReseller && (
                <Dropdown.Item className="d-flex align-items-center justify-content-between position-relative">
                  <span>
                    <FaPodcast className="me-2 text-danger" /> Enable Podcast
                  </span>

                  {loadingToggles[`${row.id}_enablePodcast`] && (
                    <Spinner
                      animation="border"
                      className="inner-spinner"
                      size="sm"
                    />
                  )}
                  <Form.Check
                    type="switch"
                    id={`podcast-switch-${row.id}`}
                    checked={!!row.attributes?.metadata?.["3"]?.enablePodcast}
                    onChange={(e) => {
                      e.stopPropagation();
                      handlePodcastToggle(
                        row,
                        "enablePodcast",
                        row.attributes?.metadata?.["3"]?.enablePodcast
                      );
                    }}
                  />
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          ) : (
            <Dropdown.Menu className="custom-action-menu shadow">
              <Dropdown.Item
                onClick={(e) => {
                  resendInvitations(row.attributes.id);
                }}
              >
                <FiRefreshCw className="me-2 text-success" />
                Regenerate
              </Dropdown.Item>
              <Dropdown.Item
                onClick={(e) => {
                  deleteInvite(row.attributes.id);
                }}
              >
                <FiTrash2 className="me-2 text-danger" />
                Delete
              </Dropdown.Item>
            </Dropdown.Menu>
          )}
        </Dropdown>
      ),
      width: "100px",
      center: "true",
    },
  ];

  return (
    <div className="section_main_div">
      <h2 className="section_heading">Invitations</h2>

      <div className="d-flex justify-content-end py-4 gap-4">
        <Button
          variant="success"
          className="px-1 d-flex align-items-center"
          onClick={(e) => {
            setStationAdminCreatePopup({ show: true });
          }}
        >
          <AddCircleOutlineIcon />{" "}
          <span className="px-2">Create Admin Owner</span>
        </Button>

        {/* <Button variant="success" className="px-1 d-flex align-items-center"
                    onClick={(e) => {
                        setStationInvitePopup({ show: true });
                    }}
                >
                    <AddCircleOutlineIcon /> <span className="px-2">SEND INVITE VIA EMAIL</span>
                </Button> */}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      <CustomPagination
        page={page}
        pages={meta?.pages}
        perPage={perPage} // ✅ value, not a function
        onPageChange={handlePageChange}
        onPerPageChange={handlePerRowsChange} // ✅ function handler
        onSearchChange={search}
      />

      <div
        className="custom-shadow position table-container position-relative"
        style={{ overflow: "visible" }}
      >
        <DataTable
          columns={columns}
          data={users ?? []}
          highlightOnHover
          theme="solarizedLight"
          customStyles={customStyles}
          responsive
          pagination={false}
        />

        {loading && (
          <div className="table-loader-overlay d-flex justify-content-center align-items-center">
            <PageLoader />
          </div>
        )}
      </div>
      <CustomPagination
        page={page}
        pages={meta?.pages}
        perPage={perPage} // ✅ value, not a function
        onPageChange={handlePageChange}
        onPerPageChange={handlePerRowsChange} // ✅ function handler
      />

      <ProjectSelectPopup
        show={projectPopup.show}
        row={projectPopup.row}
        selected={selectedProject}
        onClose={() => setProjectPopup({ show: false, row: null })}
      />

      <ModifyInvitePopup
        show={modifyPopup.show}
        row={modifyPopup.row}
        selected={modifyPopup}
        onClose={() => setModifyPopup({ show: false, row: null })}
      />

      <StationGroupPopup
        show={stationGroupPopup.show}
        row={stationGroupPopup.row}
        onClose={() => setStationGroupPopup({ show: false, row: null })}
      />

      <RoleManagement
        show={role.show}
        row={role.row}
        onClose={() => setRole({ show: false, row: null })}
      />

      <UserLookupPopup
        show={userLookupPopup.show}
        id={userLookupPopup.id}
        onClose={() => setUserLookupPopup({ show: false, id: null })}
      />

      <AddMember
        show={addMemberPopup.show}
        row={addMemberPopup.row}
        onClose={() => setAddMemberPopup({ show: false, row: null })}
      />

      <StationInvite
        show={stationInvitePopup.show}
        row={stationInvitePopup.row}
        onClose={() => setStationInvitePopup({ show: false })}
      />

      <CreateStationAdmin
        show={stationAdminCreatePopup.show}
        row={stationAdminCreatePopup.row}
        onClose={() => setStationAdminCreatePopup({ show: false })}
      />
    </div>
  );
};

export default List;
